import {
  Button,
  Dropdown,
  FormLayout,
  ImagePreviewField,
  TextInput,
} from 'components/common'
import { Control, UseFormRegister, UseFormSetValue } from 'react-hook-form'
import { activeOptions, DELIVEROO_FIELDS as DF } from '../utils'
import { getNestedErrorMessage } from 'utils/helpers/getNestedErrorMessage'
import { InputKeyPair } from 'types/common'
import { IAggregator } from 'types/aggregator'
import { useState } from 'react'
import toast from 'react-hot-toast'
import { handleError } from 'utils/helpers/handleError'
import {
  triggerDeliverooCatalogueSync,
  triggerDeliverooInventorySync,
  triggerDeliverooListingSync,
  triggerDeliverooPriceSync,
} from 'utils/api/aggregatorApi'
import StoreDropdown from 'components/userManagement/storeDropdown'

interface DeliverooAggregatorConfigProps {
  register: UseFormRegister<any>
  setValue: UseFormSetValue<any>
  control: Control<any>
  errors: any
  aggregator?: IAggregator
}

const generateName = (inputKey: InputKeyPair) => {
  return `${inputKey.parentKey}.${inputKey.key}`
}

const cronTypes = {
  MASTER: 'Master',
  INVENTORY: 'Inventory',
  PRICING: 'Pricing',
  LISTING: 'Listing',
}

const successMessage =
  'Cron trigger successful. You will be notified after completion.'

const failedMessage = 'Failed to trigger cron'

const DeliverooAggregatorConfig = ({
  register,
  errors,
  setValue,
  aggregator,
  control,
}: DeliverooAggregatorConfigProps) => {
  const [loading, setLoading] = useState({
    type: cronTypes.MASTER,
    isLoading: false,
  })

  const triggerCron = async (type: string) => {
    setLoading({ type, isLoading: true })

    const triggerToast = toast.loading(`Triggering ${type} cron..`)

    try {
      switch (type) {
        case cronTypes.MASTER:
          await triggerDeliverooCatalogueSync()
          break

        case cronTypes.INVENTORY:
          await triggerDeliverooInventorySync()
          break

        case cronTypes.PRICING:
          await triggerDeliverooPriceSync()
          break

        case cronTypes.LISTING:
          await triggerDeliverooListingSync()
          break
      }
      toast.success(successMessage, { id: triggerToast })
    } catch (error) {
      handleError(error, triggerToast, {
        default: failedMessage,
      })
    } finally {
      setLoading({ type, isLoading: false })
    }
  }

  return (
    <>
      <h3 className="title">Deliveroo Sync Config</h3>
      <FormLayout>
        <div className="flex flex-col gap-2">
          <p className="mb-1.5 text-sm font-bold text-sidebar-primary">
            Authentication Details
          </p>

          <div className="grid gap-x-10 gap-y-3 mid-md:grid-cols-3">
            <Dropdown
              required
              defaultValue={aggregator?.deliveroo?.isEnable ?? false}
              setValue={setValue}
              options={activeOptions}
              label={DF.IS_ENABLE.label}
              name={generateName(DF.IS_ENABLE)}
              error={getNestedErrorMessage(errors, DF.IS_ENABLE)}
            />
          </div>

          <div className="grid gap-x-10 gap-y-3 mid-md:grid-cols-3">
            <TextInput
              register={register}
              label={DF.BASE_URL.label}
              name={generateName(DF.BASE_URL)}
              error={getNestedErrorMessage(errors, DF.BASE_URL)}
              placeholder="e.g., https://api.deliveroo.com"
              hintText="Enter the base URL provided by Deliveroo"
            />

            <TextInput
              type="password"
              register={register}
              label={DF.WEBHOOK_SECRET.label}
              name={generateName(DF.WEBHOOK_SECRET)}
              error={getNestedErrorMessage(errors, DF.WEBHOOK_SECRET)}
              hintText="Enter the webhook secret provided by Deliveroo"
            />
          </div>

          <div className="grid gap-x-10 gap-y-3 mid-md:grid-cols-3">
            <TextInput
              register={register}
              label={DF.AUTH_BASE_URL.label}
              name={generateName(DF.AUTH_BASE_URL)}
              error={getNestedErrorMessage(errors, DF.AUTH_BASE_URL)}
              placeholder="e.g., https://auth.deliveroo.com"
              hintText="Enter the auth base URL provided by Deliveroo"
            />

            <TextInput
              register={register}
              label={DF.USERNAME.label}
              name={generateName(DF.USERNAME)}
              hintText="Enter Username for generating API token"
              error={getNestedErrorMessage(errors, DF.USERNAME)}
            />

            <TextInput
              type="password"
              register={register}
              label={DF.PASSWORD.label}
              name={generateName(DF.PASSWORD)}
              error={getNestedErrorMessage(errors, DF.PASSWORD)}
              hintText="Enter Password for generating API token"
            />
          </div>

          <hr className="my-4" />

          <p className="mb-1.5 text-sm font-bold text-sidebar-primary">Other</p>
          <div className="grid gap-x-10 gap-y-3 mid-md:grid-cols-3">
            <TextInput
              register={register}
              label={DF.BRAND_ID.label}
              name={generateName(DF.BRAND_ID)}
              error={getNestedErrorMessage(errors, DF.BRAND_ID)}
              hintText="Enter the brand ID provided by Deliveroo"
            />

            <TextInput
              register={register}
              label={DF.CATALOGUE_ID.label}
              name={generateName(DF.CATALOGUE_ID)}
              error={getNestedErrorMessage(errors, DF.CATALOGUE_ID)}
              hintText="Enter the brand ID provided by Deliveroo"
            />

            <StoreDropdown
              required
              defaultValue={aggregator?.deliveroo?.defaultPriceStoreId ?? ''}
              label={DF.DEFAULT_PRICE_STORE_ID.label}
              name={generateName(DF.DEFAULT_PRICE_STORE_ID)}
              setValue={setValue}
              error={getNestedErrorMessage(errors, DF.DEFAULT_PRICE_STORE_ID)}
              hintText="Select the store from which you want to fetch the default price"
            />

            <ImagePreviewField
              name={generateName(DF.HERO_IMAGE_URL)}
              label={DF.HERO_IMAGE_URL.label}
              control={control}
              error={getNestedErrorMessage(errors, DF.HERO_IMAGE_URL)}
              containerClass="md:col-span-2"
            />
          </div>

          <hr className="my-4" />
          <p className="mb-1.5 text-sm font-bold text-sidebar-primary">
            Manual Sync Trigger
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <Button
              buttonText="Trigger Sync Catalogue"
              size="small"
              className="w-full max-w-36 !border !py-1.5 !font-normal"
              type="button"
              variant="outline"
              onClick={() => triggerCron(cronTypes.MASTER)}
              disabled={loading.isLoading}
              loading={loading.type === cronTypes.MASTER && loading.isLoading}
            />

            <Button
              size="small"
              buttonText="Trigger Sync Inventory"
              className="w-full max-w-36 !border !py-1.5 !font-normal"
              type="button"
              variant="outline"
              onClick={() => triggerCron(cronTypes.INVENTORY)}
              disabled={loading.isLoading}
              loading={
                loading.type === cronTypes.INVENTORY && loading.isLoading
              }
            />
            <Button
              size="small"
              buttonText="Trigger Sync Price"
              className="w-full max-w-36 !border !py-1.5 !font-normal"
              type="button"
              variant="outline"
              onClick={() => triggerCron(cronTypes.PRICING)}
              disabled={loading.isLoading}
              loading={loading.type === cronTypes.PRICING && loading.isLoading}
            />
            <Button
              size="small"
              buttonText="Trigger Sync Listing"
              className="w-full max-w-36 !border !py-1.5 !font-normal"
              type="button"
              variant="outline"
              onClick={() => triggerCron(cronTypes.LISTING)}
              disabled={loading.isLoading}
              loading={loading.type === cronTypes.LISTING && loading.isLoading}
            />
          </div>
        </div>
      </FormLayout>
    </>
  )
}

export default DeliverooAggregatorConfig
